import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Pagination, AsvConsultationHdrModel, AsvConsultationHdrService } from 'blg-akaun-ts-lib';
import { Observable, combineLatest, forkJoin, iif, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, scan, startWith, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { AppConfig } from '../../visa';
import { IDropdown } from '../../models/search-model';

@Component({
  selector: 'app-consultation-no-dropdown',
  templateUrl: './consultation-no-dropdown.component.html',
  styleUrls: ['./consultation-no-dropdown.component.css']
})
export class ConsulationNoDropdownComponent implements OnInit, OnDestroy {
  @Input() control!: FormControl;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() fieldLabel = 'Consultation No.';
  @Output() selectionChange = new EventEmitter();

  private subs = new SubSink();
  apiVisa = AppConfig.apiVisa;
  filterControl = new FormControl();
  formValue$: Observable<string>;
  initialValue$: Observable<IDropdown[]>;
  both$: Observable<IDropdown[]>;
  computedInitDropdown$: Observable<IDropdown[]>;
  finalDropdown$: Observable<IDropdown[]>;
  initialDropdown$ = this.consulationService.getByCriteria(this.getBasePagination(), this.apiVisa).pipe(
    map(resp => resp?.data?.map(this.mapContainerToDropDown)),
    catchError(err => {
      console.error(err);
      return of([] as IDropdown[]);
    })
  );

  constructor(private consulationService: AsvConsultationHdrService) { }

  ngOnInit() {
    this.initialValue$ = this.consulationService.getByCriteria(this.getInitPagination(), this.apiVisa).pipe(
      map(resp => resp?.data?.map(this.mapContainerToDropDown)),
      catchError(err => {
        console.error(err);
        return of([] as IDropdown[]);
      })
    );
    
    this.both$ = forkJoin([this.initialDropdown$, this.initialValue$]).pipe(
      map(([a, b]) => [...a, ...b])
    );
    
    this.computedInitDropdown$ = iif(() => this.control.value === null, this.initialDropdown$, this.both$);
    
    this.formValue$ = this.filterControl.valueChanges.pipe(
      startWith(<string>null),
      debounceTime(300),
      distinctUntilChanged(),
    );
    
    this.finalDropdown$ = combineLatest([this.computedInitDropdown$, this.formValue$]).pipe(
      switchMap(([machineList, search]) => {
        if (!search) return of({ og: machineList, filt: null, new: false });
        
        const filter = machineList.filter(c => c.viewValue.toLowerCase().includes(search.toLowerCase()));
        
        if (filter.length) return of({ og: machineList, filt: filter, new: false });
        
        const pag = this.getBasePagination();
        pag.conditionalCriteria.push({ columnName: 'consultation_running_no', operator: '=', value: search });
        
        return this.consulationService.getByCriteria(pag, this.apiVisa).pipe(
          map(resp => resp?.data?.map(this.mapContainerToDropDown)),
          map(options => ({ og: machineList, filt: options, new: true })),
          catchError(err => {
            console.error(err);
            return of({ og: machineList, filt: [] as IDropdown[], new: false });
          })
        );
      }),
      scan((acc, cur) => {
        const initial = acc.acc.length ? acc.acc : cur.og;
        const runTot = cur.new ? [...initial, ...cur.filt] : [...initial];
        const newFinal = cur.filt ? [...new Set([...cur.filt])] : [...new Set(runTot)];

        return {
          acc: [...new Set(runTot)],
          final: newFinal
        };
      }, { acc: [], final: [] } as { acc: IDropdown[], final: IDropdown[] }),
      map(acc => acc.final)
    );
  }

  getBasePagination() {
    const pagination = new Pagination(0, 30);
    pagination.conditionalCriteria = [
      { columnName: "orderBy", operator: "=", value: "consultation_running_no" },
      { columnName: "order", operator: "=", value: "ASC" },
      { columnName: "status", operator: "=", value: "ACTIVE" },
    ];
    return pagination;
  }

  getInitPagination() {
    const pagination = this.getBasePagination();
    pagination.conditionalCriteria = this.control?.value
      ? [...pagination.conditionalCriteria, {
        columnName: 'guids', operator: '=', value: this.control?.value
      }]
      : [...pagination.conditionalCriteria];
    return pagination;
  }

  mapContainerToDropDown = (container: AsvConsultationHdrModel) => (
    {
      viewValue: container.bl_asv_consultation_hdr.consultation_running_no?.toString(),
      value: container.bl_asv_consultation_hdr.consultation_running_no?.toString()
    } as IDropdown
  );

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}